// ============================================================================
// src/stores/LicenseStore.ts - UPDATED FOR STRAPI V5
// ============================================================================

import { makeAutoObservable, runInAction } from 'mobx';
import { 
  LicenseRequest, 
  PreLicenseRequest, 
  BusinessLicense,
  Transaction,
  License,
  LicensePurchase,
  CreateLicenseRequestData,
  CreatePreLicenseRequestData,
  NegotiationMessage
} from '@/types/marketplace';
import { licenseService } from '@/services/licenseService';
import { RootStore } from '.';
import { AutoApprovalEngine, DEFAULT_RULES } from '@/services/autoApprovalEngine';
import { AutoApprovalSettings } from '@/types/autoApproval';
import { ID } from '@/types/common';

/**
 * LicenseStore - UPDATED
 * 
 * Key updates:
 * - Support 3 sourceTypes: content, public_catalog, license_option
 * - Integrate with new LicensePurchase flow
 * - Enhanced analytics and metrics
 */
export class LicenseStore {
  rootStore: RootStore;

  // ========================================
  // LICENSE REQUESTS
  // ========================================
  licenseRequests: LicenseRequest[] = [];
  preLicenseRequests: PreLicenseRequest[] = [];
  
  // Current user's requests
  myRequests: LicenseRequest[] = [];
  myPreRequests: PreLicenseRequest[] = [];
  
  // Incoming requests (for rights owners)
  incomingRequests: LicenseRequest[] = [];
  incomingPreRequests: PreLicenseRequest[] = [];

  // ========================================
  // LICENSES & PURCHASES
  // ========================================
  myLicenses: License[] = [];
  myPurchases: LicensePurchase[] = [];
  businessLicenses: BusinessLicense[] = [];
  
  // ========================================
  // TRANSACTIONS
  // ========================================
  transactions: Transaction[] = [];
  
  // ========================================
  // UI STATES
  // ========================================
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  error: string | null = null;
  
  // ========================================
  // PAGINATION
  // ========================================
  currentPage: number = 1;
  totalPages: number = 1;
  hasMore: boolean = true;

  // ========================================
  // ANALYTICS
  // ========================================
  licenseMetrics: any = null;
  revenueReport: any = null;
  licenseAnalytics: any = null;

  // ========================================
  // AUTO APPROVAL (Optional)
  // ========================================
  autoApprovalEngine: AutoApprovalEngine | null = null;
  autoApprovalSettings: AutoApprovalSettings | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  /**
   * Initialize store with user data
   */
  async initialize(userId: ID, userRole: string) {
    this.setLoading(true);
    try {
      await Promise.all([
        this.loadMyRequests(userId),
        this.loadMyLicenses(userId),
        this.loadMyPurchases(userId),
        userRole === 'right_owner' ? this.loadIncomingRequests(userId) : Promise.resolve(),
        this.loadTransactions(userId)
      ]);
    } catch (error) {
      console.error('License store initialization error:', error);
      this.setError('Không thể khởi tạo dữ liệu license');
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // LICENSE REQUEST MANAGEMENT
  // ========================================

  /**
   * Create new license request
   */
  async createLicenseRequest(data: CreateLicenseRequestData) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      // Validate request data
      const validation = licenseService.validateLicenseRequest(data);
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const request = await licenseService.createLicenseRequest(data);
      
      runInAction(() => {
        this.myRequests.unshift(request);
        this.licenseRequests.unshift(request);
      });
      
      return request;
    } catch (error: any) {
      const errorMsg = error.message || 'Không thể tạo yêu cầu license';
      this.setError(errorMsg);
      console.error('Create license request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Update license request
   */
  async updateLicenseRequest(requestId: ID, data: Partial<CreateLicenseRequestData>) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const updatedRequest = await licenseService.updateLicenseRequest(requestId, data);
      
      runInAction(() => {
        this.updateRequestInList(this.myRequests, updatedRequest);
        this.updateRequestInList(this.licenseRequests, updatedRequest);
        this.updateRequestInList(this.incomingRequests, updatedRequest);
      });
      
      return updatedRequest;
    } catch (error) {
      this.setError('Không thể cập nhật yêu cầu license');
      console.error('Update license request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Cancel license request
   */
  async cancelLicenseRequest(requestId: ID) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      await licenseService.cancelLicenseRequest(requestId);
      
      runInAction(() => {
        this.removeRequestFromList(this.myRequests, requestId);
        this.removeRequestFromList(this.licenseRequests, requestId);
      });
    } catch (error) {
      this.setError('Không thể hủy yêu cầu license');
      console.error('Cancel license request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Get license request details
   */
  async getLicenseRequestDetails(requestId: ID) {
    this.setLoading(true);
    try {
      const request = await licenseService.getLicenseRequest(requestId);
      return request;
    } catch (error) {
      this.setError('Không thể tải chi tiết yêu cầu');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Add negotiation message
   */
  async addNegotiationMessage(requestId: ID, message: NegotiationMessage) {
    this.setSubmitting(true);
    try {
      const updatedRequest = await licenseService.addNegotiationMessage(requestId, message);
      
      runInAction(() => {
        this.updateRequestInList(this.myRequests, updatedRequest);
        this.updateRequestInList(this.incomingRequests, updatedRequest);
      });
      
      return updatedRequest;
    } catch (error) {
      this.setError('Không thể gửi tin nhắn đàm phán');
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // ========================================
  // RIGHTS OWNER ACTIONS
  // ========================================

  /**
   * Approve license request
   */
  async approveLicenseRequest(requestId: ID, terms?: any) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const approvedRequest = await licenseService.approveLicenseRequest(requestId, terms);
      
      runInAction(() => {
        this.updateRequestInList(this.incomingRequests, approvedRequest);
      });
      
      return approvedRequest;
    } catch (error) {
      this.setError('Không thể phê duyệt yêu cầu');
      console.error('Approve request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Reject license request
   */
  async rejectLicenseRequest(requestId: ID, reason: string) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const rejectedRequest = await licenseService.rejectLicenseRequest(requestId, reason);
      
      runInAction(() => {
        this.updateRequestInList(this.incomingRequests, rejectedRequest);
      });
      
      return rejectedRequest;
    } catch (error) {
      this.setError('Không thể từ chối yêu cầu');
      console.error('Reject request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Bulk approve requests
   */
  async bulkApproveRequests(requestIds: ID[], terms?: any) {
    this.setSubmitting(true);
    try {
      const approved = await licenseService.bulkApproveLicenseRequests(requestIds, terms);
      
      runInAction(() => {
        approved.forEach(request => {
          this.updateRequestInList(this.incomingRequests, request);
        });
      });
      
      return approved;
    } catch (error) {
      this.setError('Không thể phê duyệt hàng loạt');
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Bulk reject requests
   */
  async bulkRejectRequests(requestIds: ID[], reason: string) {
    this.setSubmitting(true);
    try {
      const rejected = await licenseService.bulkRejectLicenseRequests(requestIds, reason);
      
      runInAction(() => {
        rejected.forEach(request => {
          this.updateRequestInList(this.incomingRequests, request);
        });
      });
      
      return rejected;
    } catch (error) {
      this.setError('Không thể từ chối hàng loạt');
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // ========================================
  // PRE-LICENSE REQUESTS
  // ========================================

  /**
   * Create pre-license request
   */
  async createPreLicenseRequest(data: CreatePreLicenseRequestData) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const preRequest = await licenseService.createPreLicenseRequest(data);
      
      runInAction(() => {
        this.myPreRequests.unshift(preRequest);
      });
      
      return preRequest;
    } catch (error) {
      this.setError('Không thể tạo pre-license request');
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Convert pre-license to full request
   */
  async convertPreLicenseToRequest(preRequestId: ID, additionalData?: any) {
    this.setSubmitting(true);
    try {
      const licenseRequest = await licenseService.convertPreLicenseToRequest(preRequestId, additionalData);
      
      runInAction(() => {
        this.myRequests.unshift(licenseRequest);
        // Update pre-request status
        const preRequest = this.myPreRequests.find(r => r.documentId === preRequestId);
        if (preRequest) {
          preRequest.preLicenseRequestStatus = 'converted';
        }
      });
      
      return licenseRequest;
    } catch (error) {
      this.setError('Không thể chuyển đổi pre-license');
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // ========================================
  // PAYMENT & TRANSACTIONS
  // ========================================

  /**
   * Process payment for license request
   */
  async processPayment(licenseRequestId: ID, paymentData: any) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const payment = await licenseService.processPayment(licenseRequestId, paymentData);
      
      // Reload requests and transactions
      await Promise.all([
        this.loadMyRequests(this.rootStore.authStore.currentUser?.documentId!),
        this.loadTransactions(this.rootStore.authStore.currentUser?.documentId!)
      ]);
      
      return payment;
    } catch (error) {
      this.setError('Không thể xử lý thanh toán');
      console.error('Process payment error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Track license usage
   */
  async trackLicenseUsage(licenseId: ID, usageData: any) {
    try {
      await licenseService.trackLicenseUsage(licenseId, usageData);
    } catch (error) {
      console.error('Track license usage error:', error);
      throw error;
    }
  }

  // ========================================
  // DATA LOADING METHODS
  // ========================================

  /**
   * Load my requests
   */
  async loadMyRequests(userId: ID) {
    this.setLoading(true);
    
    try {
      const [requests, preRequests] = await Promise.all([
        licenseService.getUserLicenseRequests(userId),
        licenseService.getPreLicenseRequests(userId, 'sent')
      ]);
      
      runInAction(() => {
        this.myRequests = requests;
        this.myPreRequests = preRequests;
      });
    } catch (error) {
      console.error('Load my requests error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Load my licenses
   */
  async loadMyLicenses(userId: ID) {
    try {
      const licenses = await licenseService.getUserLicenses(userId);
      runInAction(() => {
        this.myLicenses = licenses;
      });
    } catch (error) {
      console.error('Load my licenses error:', error);
    }
  }

  /**
   * Load my purchases
   */
  async loadMyPurchases(userId: ID) {
    try {
      // This would call marketplaceService.getUserPurchases
      // For now, we'll keep it empty as it's in MarketplaceStore
      runInAction(() => {
        this.myPurchases = [];
      });
    } catch (error) {
      console.error('Load my purchases error:', error);
    }
  }

  /**
   * Load incoming requests (for rights owners)
   */
  async loadIncomingRequests(userId: ID) {
    try {
      const [requests, preRequests] = await Promise.all([
        licenseService.getIncomingLicenseRequests(userId),
        licenseService.getPreLicenseRequests(userId, 'received')
      ]);
      
      runInAction(() => {
        this.incomingRequests = requests;
        this.incomingPreRequests = preRequests;
      });
    } catch (error) {
      console.error('Load incoming requests error:', error);
    }
  }

  /**
   * Load transactions
   */
  async loadTransactions(userId: ID) {
    try {
      const transactions = await licenseService.getUserTransactions(userId);
      runInAction(() => {
        this.transactions = transactions;
      });
    } catch (error) {
      console.error('Load transactions error:', error);
    }
  }

  /**
   * Load business licenses
   */
  async loadBusinessLicenses(userId: ID) {
    try {
      const licenses = await licenseService.getBusinessLicenses(userId);
      runInAction(() => {
        this.businessLicenses = licenses;
      });
    } catch (error) {
      console.error('Load business licenses error:', error);
    }
  }

  /**
   * Load more requests (pagination)
   */
  async loadMoreRequests(userId: ID, type: 'my' | 'incoming' = 'my') {
    if (!this.hasMore || this.isLoading) return;
    
    this.setLoading(true);
    
    try {
      const requests = type === 'my' 
        ? await licenseService.getUserLicenseRequests(userId)
        : await licenseService.getIncomingLicenseRequests(userId);
      
      runInAction(() => {
        if (type === 'my') {
          this.myRequests = [...this.myRequests, ...requests];
        } else {
          this.incomingRequests = [...this.incomingRequests, ...requests];
        }
        this.currentPage++;
        this.hasMore = requests.length > 0;
      });
    } catch (error) {
      console.error('Load more requests error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // ANALYTICS & REPORTING
  // ========================================

  /**
   * Load license metrics
   */
  async loadLicenseMetrics(userId: ID, period: 'week' | 'month' | 'year' = 'month') {
    try {
      const metrics = await licenseService.getLicenseMetrics(userId, period);
      runInAction(() => {
        this.licenseMetrics = metrics;
      });
      return metrics;
    } catch (error) {
      console.error('Load license metrics error:', error);
      throw error;
    }
  }

  /**
   * Load revenue report
   */
  async loadRevenueReport(ownerId: ID, period: 'week' | 'month' | 'year' = 'month') {
    try {
      const report = await licenseService.getRevenueReport(ownerId, period);
      runInAction(() => {
        this.revenueReport = report;
      });
      return report;
    } catch (error) {
      console.error('Load revenue report error:', error);
      throw error;
    }
  }

  /**
   * Load license analytics
   */
  async loadLicenseAnalytics(ownerId: ID, period: 'week' | 'month' | 'year' = 'month') {
    try {
      const analytics = await licenseService.getLicenseAnalytics(ownerId, period);
      runInAction(() => {
        this.licenseAnalytics = analytics;
      });
      return analytics;
    } catch (error) {
      console.error('Load license analytics error:', error);
      throw error;
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Update request in list
   */
  private updateRequestInList(list: LicenseRequest[], updatedRequest: LicenseRequest) {
    const index = list.findIndex(req => req.documentId === updatedRequest.documentId);
    if (index !== -1) {
      list[index] = updatedRequest;
    }
  }

  /**
   * Remove request from list
   */
  private removeRequestFromList(list: LicenseRequest[], requestId: ID) {
    const index = list.findIndex(req => req.documentId === requestId);
    if (index !== -1) {
      list.splice(index, 1);
    }
  }

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  /**
   * Reset store
   */
  reset() {
    this.licenseRequests = [];
    this.myRequests = [];
    this.myPreRequests = [];
    this.incomingRequests = [];
    this.incomingPreRequests = [];
    this.myLicenses = [];
    this.myPurchases = [];
    this.businessLicenses = [];
    this.transactions = [];
    this.currentPage = 1;
    this.hasMore = true;
    this.error = null;
    this.licenseMetrics = null;
    this.revenueReport = null;
    this.licenseAnalytics = null;
  }

  // ========================================
  // COMPUTED VALUES (GETTERS)
  // ========================================

  /**
   * Get pending requests
   */
  get pendingRequests() {
    return this.myRequests.filter(req => req.licenseRequestStatus === 'pending');
  }

  /**
   * Get approved requests
   */
  get approvedRequests() {
    return this.myRequests.filter(req => req.licenseRequestStatus === 'approved');
  }

  /**
   * Get rejected requests
   */
  get rejectedRequests() {
    return this.myRequests.filter(req => req.licenseRequestStatus === 'rejected');
  }

  /**
   * Get pending incoming requests
   */
  get pendingIncomingRequests() {
    return this.incomingRequests.filter(req => req.licenseRequestStatus === 'pending');
  }

  /**
   * Total pending requests count
   */
  get totalPendingRequests() {
    return this.pendingIncomingRequests.length;
  }

  /**
   * Get active licenses
   */
  get activeLicenses() {
    return this.myLicenses.filter(license => 
      license.licenseStatus === 'active' &&
      !licenseService.isLicenseExpired(license)
    );
  }

  /**
   * Get expiring soon licenses
   */
  get expiringSoonLicenses() {
    return this.myLicenses.filter(license => 
      licenseService.isLicenseExpiringSoon(license, 30)
    );
  }

  /**
   * Get expired licenses
   */
  get expiredLicenses() {
    return this.myLicenses.filter(license => 
      licenseService.isLicenseExpired(license)
    );
  }

  /**
   * Get active business licenses
   */
  get activeBusinessLicenses() {
    return this.businessLicenses.filter(license => 
      license.complianceStatus === 'compliant' &&
      (!license.renewalDate || new Date(license.renewalDate) > new Date())
    );
  }

  /**
   * Get at-risk business licenses
   */
  get atRiskBusinessLicenses() {
    return this.businessLicenses.filter(license => 
      license.complianceStatus === 'at_risk'
    );
  }

  /**
   * Get violated business licenses
   */
  get violatedBusinessLicenses() {
    return this.businessLicenses.filter(license => 
      license.complianceStatus === 'violated'
    );
  }

  /**
   * Get completed transactions
   */
  get completedTransactions() {
    return this.transactions.filter(t => t.status === 'completed');
  }

  /**
   * Get pending transactions
   */
  get pendingTransactions() {
    return this.transactions.filter(t => t.status === 'pending');
  }

  /**
   * Calculate total spent
   */
  get totalSpent() {
    return this.completedTransactions
      .filter(t => t.type === 'license_purchase')
      .reduce((sum, t) => sum + t.amount, 0);
  }

  /**
   * Calculate total earned (for rights owners)
   */
  get totalEarned() {
    return this.completedTransactions
      .filter(t => t.type === 'license_purchase')
      .reduce((sum, t) => sum + t.amount, 0);
  }

  /**
   * Get recent transactions
   */
  get recentTransactions() {
    return this.transactions
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);
  }

  /**
   * Get requests by source type
   */
  getRequestsBySourceType(sourceType: 'content' | 'public_catalog' | 'license_option') {
    return this.myRequests.filter(req => req.sourceType === sourceType);
  }

  /**
   * Get requests by status
   */
  getRequestsByStatus(status: 'pending' | 'approved' | 'rejected' | 'cancelled') {
    return this.myRequests.filter(req => req.licenseRequestStatus === status);
  }

  /**
   * Check if user has active license for content
   */
  hasActiveLicenseForContent(contentId: ID): boolean {
    return this.activeLicenses.some(license => 
      license.content?.documentId === contentId
    );
  }

  /**
   * Get license by content ID
   */
  getLicenseByContentId(contentId: ID): License | undefined {
    return this.activeLicenses.find(license => 
      license.content?.documentId === contentId
    );
  }

  /**
   * Check if user has pending request for content
   */
  hasPendingRequestForContent(contentId: ID): boolean {
    return this.pendingRequests.some(req => 
      (req.content?.documentId === contentId) ||
      (req.publicCatalog?.content?.documentId === contentId) ||
      (req.licenseOption?.public_catalog?.content?.documentId === contentId)
    );
  }

  /**
   * Get statistics summary
   */
  get statisticsSummary() {
    return {
      totalRequests: this.myRequests.length,
      pendingRequests: this.pendingRequests.length,
      approvedRequests: this.approvedRequests.length,
      rejectedRequests: this.rejectedRequests.length,
      
      totalLicenses: this.myLicenses.length,
      activeLicenses: this.activeLicenses.length,
      expiringSoon: this.expiringSoonLicenses.length,
      expired: this.expiredLicenses.length,
      
      totalTransactions: this.transactions.length,
      completedTransactions: this.completedTransactions.length,
      pendingTransactions: this.pendingTransactions.length,
      
      totalSpent: this.totalSpent,
      totalEarned: this.totalEarned,
      
      incomingPending: this.pendingIncomingRequests.length,
      incomingTotal: this.incomingRequests.length
    };
  }

  /**
   * Get approval rate
   */
  get approvalRate(): number {
    const total = this.incomingRequests.length;
    if (total === 0) return 0;
    
    const approved = this.incomingRequests.filter(r => r.licenseRequestStatus === 'approved').length;
    return Math.round((approved / total) * 100);
  }

  /**
   * Get average response time (in hours)
   */
  get averageResponseTime(): number {
    const respondedRequests = this.incomingRequests.filter(r => 
      r.licenseRequestStatus === 'approved' || r.licenseRequestStatus === 'rejected'
    );

    if (respondedRequests.length === 0) return 0;

    const totalHours = respondedRequests.reduce((sum, req) => {
      const requestDate = new Date(req.requestDate);
      const responseDate = new Date(req.approvalDate || req.createdAt);
      const hours = (responseDate.getTime() - requestDate.getTime()) / (1000 * 60 * 60);
      return sum + hours;
    }, 0);

    return Math.round(totalHours / respondedRequests.length);
  }
}

export default LicenseStore;